import { useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { RepoSelector } from "../../components/RepoSelector";
import type { ProjectContextData } from "./ProjectLayout";

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 border-l-4 border-primary pl-3 mb-5">
      <span className="material-symbols-outlined text-primary">{icon}</span>
      <h3 className="text-xl font-bold">{title}</h3>
    </div>
  );
}

function PlatformCard({
  icon,
  name,
  description,
  checked,
  onChange,
}: {
  icon: string;
  name: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={`cursor-pointer relative flex items-center gap-4 p-4 rounded-lg border transition-all ${
        checked
          ? "border-primary bg-primary/5 hover:bg-primary/10"
          : "border-border bg-card hover:border-muted-foreground/50 hover:bg-muted/50"
      }`}
    >
      <div
        className={`size-10 rounded-full flex items-center justify-center border ${
          checked
            ? "bg-card text-primary border-border shadow-sm"
            : "bg-muted text-muted-foreground border-border"
        }`}
      >
        <span className="material-symbols-outlined">{icon}</span>
      </div>
      <div className="flex-1">
        <p className={`font-medium ${checked ? "font-bold" : ""}`}>{name}</p>
        <p className="text-muted-foreground text-sm">{description}</p>
      </div>
      <div className={checked ? "text-primary" : "text-muted-foreground/30"}>
        <span className="material-symbols-outlined">
          {checked ? "check_circle" : "circle"}
        </span>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
    </label>
  );
}

export function ProjectSettingsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { project, setProject } = useOutletContext<ProjectContextData>();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state for settings
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || "");
  const [githubBranch, setGithubBranch] = useState(project.githubBranch);
  const [projectPath, setProjectPath] = useState(project.projectPath);
  const [buildWindows, setBuildWindows] = useState(project.buildWindows);
  const [buildLinux, setBuildLinux] = useState(project.buildLinux);
  const [buildMacos, setBuildMacos] = useState(project.buildMacos);
  const [triggerOnPush, setTriggerOnPush] = useState(project.triggerOnPush);
  // GitHub App integration
  const [githubRepoFullName, setGithubRepoFullName] = useState<string | null>(
    project.githubRepoFullName
  );
  const [githubInstallationId, setGithubInstallationId] = useState<number | null>(
    project.githubInstallationId
  );
  const [githubRepoId, setGithubRepoId] = useState<number | null>(project.githubRepoId);

  const handleSaveSettings = async () => {
    if (!id) return;
    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`/api/projects/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name,
          description: description || undefined,
          githubBranch,
          projectPath,
          buildWindows,
          buildLinux,
          buildMacos,
          triggerOnPush,
          githubRepoFullName,
          githubInstallationId,
          githubRepoId,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to save settings");
      }

      const { data: updatedProject } = await response.json();
      setProject(updatedProject);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!id) return;
    if (
      !confirm("Are you sure you want to delete this project? This action cannot be undone.")
    ) {
      return;
    }

    try {
      const response = await fetch(`/api/projects/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to delete project");
      }

      navigate("/projects");
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    }
  };

  return (
    <div className="space-y-8 pb-20">
      {error && (
        <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg">
          {error}
        </div>
      )}

      {/* General Information */}
      <section>
        <SectionHeader icon="tune" title="General Information" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 rounded-xl bg-card border border-border shadow-sm">
          <div className="flex flex-col">
            <Label htmlFor="name" className="text-base font-medium pb-2">
              Project Name
            </Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-12"
            />
          </div>

          <div className="flex flex-col md:col-span-2">
            <Label htmlFor="description" className="text-base font-medium pb-2">
              Description
            </Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of your game project..."
              className="min-h-24"
            />
          </div>
        </div>
      </section>

      {/* Source Control */}
      <section>
        <SectionHeader icon="source" title="Source Control" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 rounded-xl bg-card border border-border shadow-sm">
          <div className="flex flex-col">
            <div className="flex justify-between items-center pb-2">
              <Label className="text-base font-medium">GitHub Repository</Label>
              {githubRepoFullName && (
                <span className="text-xs text-emerald-600 font-bold uppercase tracking-wider flex items-center gap-1">
                  <span className="material-symbols-outlined text-[14px]">check_circle</span>{" "}
                  Connected
                </span>
              )}
            </div>
            <RepoSelector
              value={githubRepoFullName || undefined}
              selectedBranch={githubBranch}
              onChange={(repo) => {
                if (repo) {
                  setGithubRepoFullName(repo.repoFullName);
                  setGithubInstallationId(repo.installationId);
                  setGithubRepoId(repo.repoId);
                } else {
                  setGithubRepoFullName(null);
                  setGithubInstallationId(null);
                  setGithubRepoId(null);
                }
              }}
              onBranchChange={setGithubBranch}
            />
          </div>

          <div className="flex flex-col md:col-span-2">
            <Label htmlFor="projectPath" className="text-base font-medium pb-2">
              Path to project.godot
            </Label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-muted-foreground">
                <span className="material-symbols-outlined text-[20px]">folder_open</span>
              </span>
              <Input
                id="projectPath"
                value={projectPath}
                onChange={(e) => setProjectPath(e.target.value)}
                placeholder="./"
                className="h-12 pl-10 font-mono"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              The directory containing your project.godot file, relative to the repository root
            </p>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Platform Targets */}
        <section>
          <SectionHeader icon="devices" title="Platform Targets" />
          <div className="flex flex-col gap-3 p-6 rounded-xl bg-card border border-border shadow-sm">
            <PlatformCard
              icon="window"
              name="Windows"
              description="x86_64, Windows 10+"
              checked={buildWindows}
              onChange={setBuildWindows}
            />
            <PlatformCard
              icon="terminal"
              name="Linux"
              description="x86_64, Ubuntu 20.04+"
              checked={buildLinux}
              onChange={setBuildLinux}
            />
            <PlatformCard
              icon="desktop_mac"
              name="macOS"
              description="Universal (Apple Silicon + Intel)"
              checked={buildMacos}
              onChange={setBuildMacos}
            />
          </div>
        </section>

        {/* Build Triggers */}
        <section>
          <SectionHeader icon="bolt" title="Build Triggers" />
          <div className="flex flex-col gap-4 p-6 rounded-xl bg-card border border-border shadow-sm">
            <p className="text-muted-foreground text-sm mb-2">
              Automatically trigger builds based on repository events.
            </p>
            <div className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
              <div className="flex flex-col">
                <span className="font-medium">Push to Branch</span>
                <span className="text-muted-foreground text-xs">
                  Build on every commit to '{githubBranch || "main"}'
                </span>
              </div>
              <Switch checked={triggerOnPush} onCheckedChange={setTriggerOnPush} />
            </div>
          </div>
        </section>
      </div>

      {/* Footer Actions */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pt-8 border-t border-border">
        <button
          type="button"
          onClick={handleDeleteProject}
          className="text-destructive hover:text-destructive/80 text-sm font-medium transition-colors flex items-center gap-2 px-2 py-1 rounded hover:bg-destructive/10"
        >
          <span className="material-symbols-outlined text-lg">delete</span>
          Delete Project
        </button>
        <div className="flex gap-4 w-full sm:w-auto">
          <Button
            variant="outline"
            onClick={() => navigate("/projects")}
            className="flex-1 sm:flex-none h-12 px-6"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSettings}
            disabled={saving}
            className="flex-1 sm:flex-none h-12 px-6 shadow-lg shadow-primary/20"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
